'use client';

import { motion } from 'motion/react';
import React from 'react';

import { ButtonGroupBlock } from '@/blocks/ButtonGroupBlock/Component';
import { CMSLink } from '@/components/Link';
import RichText from '@/components/RichText';
import { useRevealNodeAnimation } from '@/hooks/animations/useRevealNodeAnimation';
import { useRevealTextAnimation } from '@/hooks/animations/useRevealTextAnimation';
import { useSplitTextAnimation } from '@/hooks/animations/useSplitTextAnimation';
import { Hero } from '@/payload-types';
import { cn } from '@/utils/cn';

import styles from './HeroContent.module.scss';

type Props = Hero & {
  locale?: string;
  className?: string;
  children?: React.ReactNode;
  startDelay?: number;
  noMaxWidth?: boolean;
  noPadding?: boolean;
};

export const HeroContent = React.memo(
  React.forwardRef<HTMLDivElement, Props>((props, ref) => {
    const {
      alignment,
      buttons,
      className,
      content,
      heading,
      headingAppearance,
      inverted,
      label,
      labelhasLink,
      link,
      locale,
      noMaxWidth,
      noPadding,
      preheader,
      secondaryHeading,
      secondaryHeadingAppearance,
      startDelay,
      type,
    } = props;

    const { Component: HeadingComponent } = useSplitTextAnimation({
      text: heading,
      inverted,
      startDelay,
    });

    const { Component: PreheaderComponent } = useRevealTextAnimation({
      text: preheader || '',
      type: 'lines',
      delay: 0.2,
      startDelay,
    });

    const { Component: SecondaryHeadingComponent } = useRevealTextAnimation({
      text: secondaryHeading || '',
      type: 'lines',
      delay: 0.25,
      startDelay,
    });

    const { ref: labelRef, variants: labelVariants } = useRevealNodeAnimation({
      yOffset: 12,
      delay: 0.2,
      startDelay,
    });

    const { ref: contentRef, variants: contentVariants } =
      useRevealNodeAnimation({
        delay: 0.5,
        startDelay: startDelay,
      });

    const { ref: buttonsRef, variants: buttonsVariants } =
      useRevealNodeAnimation({
        delay: 0.9,
        startDelay: startDelay,
      });

    const { ref: frontpageContentRef, variants: frontpageContentVariants } =
      useRevealNodeAnimation({
        delay: 0.5,
        startDelay: 0,
      });

    const { ref: frontpageButtonsRef, variants: frontpageButtonsVariants } =
      useRevealNodeAnimation({
        delay: 0.9,
        startDelay: 0,
      });

    const { ref: childrenRef, variants: childrenVariants } =
      useRevealNodeAnimation();

    const LabelTag = labelhasLink ? CMSLink : 'span';

    return (
      <div
        ref={ref}
        className={cn(
          styles.content,
          alignment && styles[alignment],
          inverted && styles[inverted],
          noPadding && styles.noPadding,
          noMaxWidth && styles.noMaxWidth,
          className,
          type === 'frontPage' && styles.frontPage
        )}
      >
        {label && (
          <motion.div
            ref={labelRef as React.RefObject<HTMLDivElement>}
            initial={'hidden'}
            animate={'visible'}
            style={{ willChange: 'opacity, transform' }}
            variants={labelVariants}
          >
            <LabelTag
              className={styles.label}
              locale={locale}
              {...(labelhasLink && { ...link })}
            >
              {labelhasLink && (
                <svg
                  width='12'
                  height='13'
                  viewBox='0 0 12 13'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path d='M10.5001 6.1011C10.5001 6.20056 10.4605 6.29594 10.3902 6.36626C10.3199 6.43659 10.2245 6.4761 10.1251 6.4761H2.78021L5.51536 9.21079C5.5502 9.24563 5.57784 9.28699 5.5967 9.33251C5.61555 9.37804 5.62526 9.42683 5.62526 9.4761C5.62526 9.52537 5.61555 9.57416 5.5967 9.61969C5.57784 9.66521 5.5502 9.70657 5.51536 9.74141C5.48052 9.77625 5.43916 9.80389 5.39364 9.82275C5.34811 9.8416 5.29932 9.85131 5.25005 9.85131C5.20078 9.85131 5.15199 9.8416 5.10646 9.82275C5.06094 9.80389 5.01958 9.77625 4.98474 9.74141L1.60974 6.36641C1.57487 6.33158 1.54721 6.29023 1.52834 6.2447C1.50947 6.19918 1.49976 6.15038 1.49976 6.1011C1.49976 6.05182 1.50947 6.00302 1.52834 5.9575C1.54721 5.91197 1.57487 5.87061 1.60974 5.83579L4.98474 2.46079C5.0551 2.39042 5.15054 2.35089 5.25005 2.35089C5.34956 2.35089 5.445 2.39042 5.51536 2.46079C5.58573 2.53115 5.62526 2.62659 5.62526 2.7261C5.62526 2.82561 5.58573 2.92105 5.51536 2.99141L2.78021 5.7261H10.1251C10.2245 5.7261 10.3199 5.76561 10.3902 5.83593C10.4605 5.90626 10.5001 6.00164 10.5001 6.1011Z' />
                </svg>
              )}
              {label}
            </LabelTag>
          </motion.div>
        )}
        {preheader && (
          <div className={styles.preheader}>
            <PreheaderComponent />
          </div>
        )}
        {heading && (
          <h1 className={cn(styles.heading, headingAppearance || 'h2')}>
            <HeadingComponent />
          </h1>
        )}
        {secondaryHeading && (
          <div
            className={cn(
              styles.secondaryHeading,
              secondaryHeadingAppearance ? secondaryHeadingAppearance : 'p'
            )}
          >
            <SecondaryHeadingComponent />
          </div>
        )}
        {type === 'frontPage' && (
          <>
            {content && content?.root?.children?.length > 0 && (
              <motion.div
                ref={frontpageContentRef as React.RefObject<HTMLDivElement>}
                className={styles.mobileContentRte}
                initial={'hidden'}
                animate={'visible'}
                style={{ willChange: 'opacity, transform' }}
                variants={frontpageContentVariants}
              >
                <RichText content={content} enableGutter={false} />
              </motion.div>
            )}
            {buttons && buttons.length > 0 && (
              <motion.div
                className={styles.mobileButtons}
                ref={frontpageButtonsRef as React.RefObject<HTMLDivElement>}
                initial={'hidden'}
                animate={'visible'}
                style={{ willChange: 'opacity, transform' }}
                variants={frontpageButtonsVariants}
              >
                <ButtonGroupBlock
                  alignment={alignment}
                  buttons={buttons}
                  blockType={'buttonGroup'}
                />
              </motion.div>
            )}
          </>
        )}
        {content && content?.root?.children?.length > 0 && (
          <motion.div
            ref={contentRef as React.RefObject<HTMLDivElement>}
            className={styles.defaultContentRte}
            initial={'hidden'}
            animate={'visible'}
            style={{ willChange: 'opacity, transform' }}
            variants={contentVariants}
          >
            <RichText content={content} enableGutter={false} />
          </motion.div>
        )}
        {buttons && buttons.length > 0 && (
          <motion.div
            className={styles.defaultButtons}
            ref={buttonsRef as React.RefObject<HTMLDivElement>}
            initial={'hidden'}
            animate={'visible'}
            style={{ willChange: 'opacity, transform' }}
            variants={buttonsVariants}
          >
            <ButtonGroupBlock
              alignment={alignment}
              buttons={buttons}
              blockType={'buttonGroup'}
            />
          </motion.div>
        )}
        {props.children && (
          <motion.div
            ref={childrenRef as React.RefObject<HTMLDivElement>}
            initial={'hidden'}
            animate={'visible'}
            style={{ willChange: 'opacity, transform' }}
            variants={childrenVariants}
          >
            {props.children}
          </motion.div>
        )}
      </div>
    );
  })
);

HeroContent.displayName = 'HeroContent';
